import { parseThemeColor, Text, useMantineTheme } from '@mantine/core';
import { forwardRef, HTMLAttributes, ReactNode, useEffect, useRef } from 'react';

import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import {
	AdvancedSwitchCheckbox,
	AdvancedSwitchInput,
	AdvancedSwitchStyles,
} from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.styles.tsx';
import {
	AdvancedSwitchAlign,
	AdvancedSwitchJustify,
	AdvancedSwitchSize,
	SwitchState,
} from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.types.ts';

interface Props extends Omit<HTMLAttributes<HTMLLabelElement>, 'onChange'> {
	size?: AdvancedSwitchSize;
	className?: string;
	leftText?: ReactNode;
	rightText?: ReactNode;
	twoWay?: boolean;
	leftValue?: unknown;
	rightValue?: unknown;
	selectedValue?: unknown;
	onChange?: (value?: unknown) => void;
	disabled?: boolean;
	disabledWithMiddleState?: boolean;
	selectedColor?: string;
	selectedFontWeight?: 400 | 600;
	unselectedColor?: string;
	unselectedFontWeight?: 400 | 600;
	unselectedLetterSpacing?: string;
	dataTestId?: string;
	justify?: AdvancedSwitchJustify;
	align?: AdvancedSwitchAlign;
}

export const AdvancedSwitch = forwardRef<HTMLLabelElement, Props>(function AdvancedSwitch(
	{
		size = 's',
		twoWay = false,
		leftValue = false,
		rightValue = true,
		selectedValue,
		leftText,
		rightText,
		disabled,
		disabledWithMiddleState,
		onChange,
		className,
		selectedColor,
		selectedFontWeight = 600,
		unselectedColor,
		unselectedFontWeight = 400,
		unselectedLetterSpacing = '0.20px',
		dataTestId,
		justify = 'flex-start',
		align = 'center',
		// Belongs on the real checkbox, not the wrapper, so the switch role gets the name.
		'aria-label': ariaLabel,
		// Residual props (e.g. the hover/focus handlers + aria that Mantine
		// `Tooltip` injects onto its target) are forwarded to the root so this
		// component can act as a Tooltip target.
		...rest
	}: Props,
	ref
) {
	const getState = () => {
		if (disabledWithMiddleState) {
			return SwitchState.MIDDLE;
		}

		if (selectedValue === leftValue) {
			return SwitchState.LEFT;
		}

		if (selectedValue === rightValue) {
			return SwitchState.RIGHT;
		}

		return SwitchState.MIDDLE;
	};

	const state = getState();

	const inputRef = useRef<HTMLInputElement>(null);
	// `indeterminate` is a DOM property, not an attribute — set it imperatively
	// so the MIDDLE state is reflected on the real checkbox.
	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.indeterminate = state === SwitchState.MIDDLE;
		}
	}, [state]);

	const mantineTheme = useMantineTheme();
	const resolveColor = (color: string | undefined, fallback: string) =>
		color ? parseThemeColor({ color, theme: mantineTheme }).value : fallback;

	const selectedTextStyles = {
		color: `${resolveColor(selectedColor, mantineTheme.colors.blue[6])} !important`,
		fontWeight: selectedFontWeight,
		letterSpacing: 0,
	};

	const notSelectedTextStyles = {
		color: `${resolveColor(unselectedColor, mantineTheme.colors.gray[9])} !important`,
		fontWeight: unselectedFontWeight,
		letterSpacing: unselectedLetterSpacing,
	};

	// Emit the next value based on the current visual state (not the DOM
	// checkbox's toggled value) so 2-state, arbitrary-value, and 3-state
	// (cycling) consumers all keep their existing semantics.
	const emitToggle = () => {
		if (disabled) {
			return;
		}

		const nextValue =
			state === SwitchState.MIDDLE || state === SwitchState.LEFT ? rightValue : leftValue;

		onChange?.(nextValue);
	};

	return (
		<AdvancedSwitchStyles
			as="label"
			ref={ref}
			{...rest}
			className={className}
			disabled={disabled}
			justify={justify}
			align={align}
		>
			<AdvancedSwitchCheckbox
				ref={inputRef}
				type="checkbox"
				role="switch"
				checked={state === SwitchState.RIGHT}
				disabled={disabled}
				onChange={emitToggle}
				aria-label={ariaLabel}
				data-testid={dataTestId}
			/>

			{leftText && (
				<Text
					style={state === SwitchState.LEFT ? selectedTextStyles : notSelectedTextStyles}
				>
					{leftText}
				</Text>
			)}

			<AdvancedSwitchInput
				aria-hidden="true"
				state={state}
				data-testid={TEST_ELEMENT_IDS.CAP_SELECTION_TOGGLE_SWITCH}
				data-state={state}
				size={size}
				twoWay={twoWay}
			/>

			{rightText && (
				<Text
					style={state === SwitchState.RIGHT ? selectedTextStyles : notSelectedTextStyles}
				>
					{rightText}
				</Text>
			)}
		</AdvancedSwitchStyles>
	);
});
